import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Footer Widget - Simplified and responsive footer
 *
 * Features:
 * - Streamlined company info
 * - Contact information
 * - Footer navigation links
 * - Copyright information
 * - Fully responsive design (mobile-first)
 */
export function Footer() {
  const navigate = useNavigate();

  const navigationLinks = [
    { label: "회사소개", path: "/about" },
    { label: "이용약관", path: "/terms" },
    { label: "개인정보 취급방침", path: "/privacy" },
    { label: "이용안내", path: "/guide" },
  ];

  return (
    <footer className="w-full bg-white py-10">
      {/* Mobile Layout */}
      <div className="md:hidden px-4 py-8">
        {/* Contact Info - Top */}
        <div className="space-y-3 mb-6">
          {/* Phone */}
          <div className="flex items-center gap-2">
            <svg
              width="17"
              height="18"
              viewBox="0 0 17 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.3682 7.5C14.9557 3.98069 11.9634 1.25 8.33333 1.25C4.70324 1.25 1.71094 3.98069 1.2985 7.5H3.75C4.67048 7.5 5.41667 8.24617 5.41667 9.16667V12.5C5.41667 13.4205 4.67048 14.1667 3.75 14.1667H1.66667C0.746192 14.1667 0 13.4205 0 12.5V8.33333C0 3.73096 3.73096 0 8.33333 0C12.9357 0 16.6667 3.73096 16.6667 8.33333V14.1667C16.6667 16.0076 15.1742 17.5 13.3333 17.5H8.95833C8.61317 17.5 8.33333 17.2202 8.33333 16.875C8.33333 16.5298 8.61317 16.25 8.95833 16.25H13.3333C14.4839 16.25 15.4167 15.3172 15.4167 14.1667H12.9167C11.9962 14.1667 11.25 13.4205 11.25 12.5V9.16667C11.25 8.24617 11.9962 7.5 12.9167 7.5H15.3682ZM12.9167 8.75H15.4167V12.9167H12.9167C12.6866 12.9167 12.5 12.7301 12.5 12.5V9.16667C12.5 8.93658 12.6866 8.75 12.9167 8.75ZM1.25 12.5C1.25 12.7301 1.43655 12.9167 1.66667 12.9167H3.75C3.98012 12.9167 4.16667 12.7301 4.16667 12.5V9.16667C4.16667 8.93658 3.98012 8.75 3.75 8.75H1.25V12.5Z"
                fill="#555555"
              />
            </svg>
            <span className="text-xs text-gray-600">학습 및 창업문의</span>
            <span className="text-sm font-semibold text-gray-900">
              051-787-3291
            </span>
          </div>

          {/* Hours */}
          <div className="text-xs text-gray-600 space-y-1">
            <p>평일 | 10:00 ~ 17:00 (점심시간 | 11:30 ~ 12:30)</p>
            <p>주말/공휴일 휴무</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* Company Info */}
        <div className="space-y-2 text-xs text-gray-600 leading-relaxed mb-6">
          <p>
            <span className="font-semibold text-gray-900">법인명(상호)</span>:
            주식회사 운복 |{" "}
            <span className="font-semibold text-gray-900">대표자</span>: 조일현
          </p>
          <p>
            <span className="font-semibold text-gray-900">전화</span>:
            051-781-3291
          </p>
          <p>
            <span className="font-semibold text-gray-900">
              개인정보관리책임자
            </span>
            : 조일현
          </p>
          <p>
            <span className="font-semibold text-gray-900">사업자등록번호</span>:
            761-87-01634{" "}
            <span className="font-semibold text-gray-900">통신판매업신고</span>:
            제2020-부산해운대-0938호{" "}
            <button className="text-gray-600 hover:text-gray-900 underline transition-colors">
              [사업자정보확인]
            </button>
          </p>
          <p>
            <span className="font-semibold text-gray-900">주소</span>: 부산
            해운대구 센텀중앙로 78 센텀그린타워 905호
          </p>
          <p>
            <span className="font-semibold text-gray-900">이메일</span>:
            angpapa@naver.com
          </p>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block bg-white w-full">
        <div className="max-w-6xl mx-auto">
          {/* Top Section - 2 Column Layout */}
          <div className="grid grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            {/* Left - Company Info */}
            <div className="col-span-2 min-w-0">
              <div className="space-y-3 text-xs md:text-sm text-gray-600 leading-relaxed break-words">
                <p>
                  <span className="font-semibold text-gray-900">
                    법인명(상호)
                  </span>
                  : 주식회사 운복 |{" "}
                  <span className="font-semibold text-gray-900">대표자</span>:
                  조일현 |{" "}
                  <span className="font-semibold text-gray-900">전화</span>:
                  051-781-3291 |{" "}
                  <span className="font-semibold text-gray-900">
                    개인정보관리책임자
                  </span>
                  : 조일현
                </p>
                <p>
                  <span className="font-semibold text-gray-900">
                    사업자등록번호
                  </span>
                  : 761-87-01634 |{" "}
                  <span className="font-semibold text-gray-900">
                    통신판매업신고
                  </span>
                  : 제2020-부산해운대-0938호{" "}
                  <button className="text-gray-600 hover:text-gray-900 underline transition-colors">
                    [사업자정보확인]
                  </button>
                </p>
                <p>
                  <span className="font-semibold text-gray-900">주소</span>:
                  부산 해운대구 센텀중앙로 78 센텀그린타워 905호
                </p>
                <p>
                  <span className="font-semibold text-gray-900">이메일</span>:
                  angpapa@naver.com
                </p>
              </div>
            </div>

            {/* Right - Contact Info */}
            <div className="col-span-1 space-y-4 min-w-0">
              {/* Contact Header with Icon */}
              <div className="flex items-center">
                <svg
                  width="17"
                  height="18"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0 mt-0.5"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.3682 7.5C14.9557 3.98069 11.9634 1.25 8.33333 1.25C4.70324 1.25 1.71094 3.98069 1.2985 7.5H3.75C4.67048 7.5 5.41667 8.24617 5.41667 9.16667V12.5C5.41667 13.4205 4.67048 14.1667 3.75 14.1667H1.66667C0.746192 14.1667 0 13.4205 0 12.5V8.33333C0 3.73096 3.73096 0 8.33333 0C12.9357 0 16.6667 3.73096 16.6667 8.33333V14.1667C16.6667 16.0076 15.1742 17.5 13.3333 17.5H8.95833C8.61317 17.5 8.33333 17.2202 8.33333 16.875C8.33333 16.5298 8.61317 16.25 8.95833 16.25H13.3333C14.4839 16.25 15.4167 15.3172 15.4167 14.1667H12.9167C11.9962 14.1667 11.25 13.4205 11.25 12.5V9.16667C11.25 8.24617 11.9962 7.5 12.9167 7.5H15.3682ZM12.9167 8.75H15.4167V12.9167H12.9167C12.6866 12.9167 12.5 12.7301 12.5 12.5V9.16667C12.5 8.93658 12.6866 8.75 12.9167 8.75ZM1.25 12.5C1.25 12.7301 1.43655 12.9167 1.66667 12.9167H3.75C3.98012 12.9167 4.16667 12.7301 4.16667 12.5V9.16667C4.16667 8.93658 3.98012 8.75 3.75 8.75H1.25V12.5Z"
                    fill="#555555"
                  />
                </svg>
                <div className="flex-1 items-start">
                  <span className="text-xs text-gray-600 ml-2">
                    학습 및 창업문의
                  </span>
                  <span className="text-lg font-semibold text-gray-900 flex-1 items-end ml-2">
                    051-787-3291
                  </span>
                </div>
              </div>

              {/* Hours */}
              <div className="space-y-1 text-xs text-gray-600">
                <p>평일 | 10:00 ~ 17:00</p>
                <p>(점심시간 | 11:30 ~ 12:30)</p>
                <p>주말/공휴일 휴무</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Navigation & Copyright */}
      <div className="bg-[#F7F7F7] py-2 md:py-2 mb-[0px] md:mb-[60px] w-full">
        <div className="w-full px-4 flex items-center justify-start gap-2 md:gap-6 flex-wrap md:max-w-6xl md:mx-auto">
          {navigationLinks.map((link, index) => (
            <React.Fragment key={link.path}>
              <button
                onClick={() => navigate(link.path)}
                className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium whitespace-nowrap flex-shrink-0"
              >
                {link.label}
              </button>
              {index < navigationLinks.length - 1 && (
                <span className="text-gray-300 flex-shrink-0">|</span>
              )}
            </React.Fragment>
          ))}

          <span className="text-xs md:text-sm text-gray-500 md:ml-auto whitespace-nowrap flex-shrink-0">
            Copyright © GGP. All Rights Reserved.
          </span>
        </div>
      </div>
    </footer>
  );
}
